/**
 * Cliente API Centralizado
 *
 * Proporciona un cliente HTTP único con:
 * - Gestión automática de autenticación
 * - Construcción de URLs con parámetros
 * - Manejo consistente de errores
 * - Configuración centralizada
 *
 * USO:
 *   import { apiClient } from '@/services/apiClient';
 *   import { API_ENDPOINTS } from '@/config/apiEndpoints';
 *
 *   // GET con parámetros de ruta
 *   apiClient.get(API_ENDPOINTS.PLANS_DETAIL, { plan_id: 123 });
 *
 *   // GET con query params
 *   apiClient.get(API_ENDPOINTS.PLANS_LIST, {}, { page: 1, limit: 10 });
 *
 *   // POST con datos
 *   apiClient.post(API_ENDPOINTS.PLANS_LIST, { name: 'Nuevo Plan' });
 *
 *   // PUT con parámetros de ruta y datos
 *   apiClient.put(API_ENDPOINTS.PLANS_DETAIL, { plan_id: 123 }, { name: 'Actualizado' });
 *
 *   // DELETE con parámetros de ruta
 *   apiClient.delete(API_ENDPOINTS.PLANS_DETAIL, { plan_id: 123 });
 */

import axios from 'axios';
import i18n from '@/i18n';

// Timeout global para evitar requests colgados
axios.defaults.timeout = 15000;

/**
 * Obtiene la URL base de la API
 * DETECCIÓN AUTOMÁTICA EN RUNTIME - NO depende de VITE_API_URL
 */
const isBrowser = typeof window !== 'undefined';

const shouldUseDevProxy = () => {
  if (!isBrowser) return false;
  const hostname = window.location.hostname;
  const isViteDev =
    typeof import.meta !== 'undefined' &&
    import.meta?.env &&
    Boolean(import.meta.env.DEV);

  if (!isViteDev) return false;

  // En localhost preferimos ir directo al backend para evitar cuelgues del proxy
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }

  // Cuando corremos Vite dev server (puerto 3010 en docker), usar mismo origen
  const devPorts = new Set(['3010', '5173']);
  return devPorts.has(window.location.port);
};

const getBaseApiUrl = () => {
  if (shouldUseDevProxy()) {
    // En desarrollo usamos el proxy de Vite -> llamadas relativas a /api
    return '';
  }

  const hostname = isBrowser ? window.location.hostname : '';
  const protocol = isBrowser ? window.location.protocol : 'http:';

  // 1. RAILWAY: Detectar por hostname *.railway.app
  const isRailway = hostname.endsWith('.railway.app');
  if (isRailway) {
    return 'https://smarthub-backend-production.up.railway.app';
  }

  // 2. LOCALHOST: Desarrollo local fuera de docker
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8010';
  }

  // 3. DOCKER INTERNO: IP interna de Docker (172.x.x.x)
  if (hostname.startsWith('172.')) {
    return 'http://backend:8010';
  }

  // 4. RED LOCAL: IPs privadas (192.168.x.x, 10.x.x.x)
  return `${protocol}//${hostname}:8010`;
};

/**
 * Construye URL completa reemplazando parámetros de ruta
 *
 * @param {string} endpoint - Endpoint con parámetros (ej: '/api/plans/:plan_id')
 * @param {Object} pathParams - Parámetros de ruta (ej: { plan_id: 123 })
 * @returns {string} URL completa con parámetros reemplazados
 *
 * @example
 * buildUrl('/api/plans/:plan_id', { plan_id: 123 })
 * // => 'http://localhost:8010/api/plans/123'
 */
const buildUrl = (endpoint, pathParams = {}) => {
  const baseUrl = getBaseApiUrl();
  let url = `${baseUrl}${endpoint}`;

  // Reemplazar parámetros en la URL
  Object.keys(pathParams).forEach(key => {
    url = url.replace(`:${key}`, encodeURIComponent(pathParams[key]));
  });

  // Validar que no queden parámetros sin reemplazar
  const unreplacedParams = url.match(/:[a-zA-Z_]+/g);
  if (unreplacedParams) {
    console.warn(
      `[apiClient] Parámetros no reemplazados en URL: ${unreplacedParams.join(', ')}`,
      { endpoint, pathParams, finalUrl: url }
    );
  }

  return url;
};

/**
 * Obtiene headers de autenticación con token
 *
 * @returns {Object} Headers con Authorization si el token existe
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  const headers = {
    'Accept-Language': i18n.language || 'es',
  };

  // Solo agregar Authorization si el token existe
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Maneja errores de API de forma consistente
 *
 * @param {Error} error - Error de axios
 * @returns {Object} Objeto de error normalizado
 */
const handleError = (error) => {
  const response = error.response;

  if (!response) {
    // Error de red
    return {
      error: true,
      message: 'Error de conexión con el servidor',
      code: 'NETWORK_ERROR',
      originalError: error,
    };
  }

  // Error HTTP
  const message = response.data?.detail ||
                 response.data?.message ||
                 response.statusText ||
                 'Error desconocido';

  return {
    error: true,
    message,
    code: response.data?.code || `HTTP_${response.status}`,
    status: response.status,
    data: response.data,
    originalError: error,
  };
};

/**
 * Cliente API Centralizado
 */
export const apiClient = {
  /**
   * GET request
   *
   * @param {string} endpoint - Endpoint de API_ENDPOINTS
   * @param {Object} pathParams - Parámetros de ruta (ej: { plan_id: 123 })
   * @param {Object} queryParams - Parámetros de query (ej: { page: 1, limit: 10 })
   * @param {Object} config - Configuración adicional de axios
   * @returns {Promise} Promesa con respuesta de axios
   */
  get: (endpoint, pathParams = {}, queryParams = {}, config = {}) => {
    const url = buildUrl(endpoint, pathParams);
    return axios.get(url, {
      headers: getAuthHeaders(),
      params: queryParams,
      ...config,
    });
  },

  /**
   * POST request
   *
   * @param {string} endpoint - Endpoint de API_ENDPOINTS
   * @param {Object} pathParams - Parámetros de ruta (ej: { plan_id: 123 })
   * @param {Object} data - Datos a enviar en el body
   * @param {Object} config - Configuración adicional de axios
   * @returns {Promise} Promesa con respuesta de axios
   */
  post: (endpoint, pathParams = {}, data = {}, config = {}) => {
    const url = buildUrl(endpoint, pathParams);
    return axios.post(url, data, {
      headers: getAuthHeaders(),
      ...config,
    });
  },

  /**
   * PUT request
   *
   * @param {string} endpoint - Endpoint de API_ENDPOINTS
   * @param {Object} pathParams - Parámetros de ruta (ej: { plan_id: 123 })
   * @param {Object} data - Datos a enviar en el body
   * @param {Object} config - Configuración adicional de axios
   * @returns {Promise} Promesa con respuesta de axios
   */
  put: (endpoint, pathParams = {}, data = {}, config = {}) => {
    const url = buildUrl(endpoint, pathParams);
    return axios.put(url, data, {
      headers: getAuthHeaders(),
      ...config,
    });
  },

  /**
   * PATCH request
   *
   * @param {string} endpoint - Endpoint de API_ENDPOINTS
   * @param {Object} pathParams - Parámetros de ruta (ej: { plan_id: 123 })
   * @param {Object} data - Datos a enviar en el body
   * @param {Object} config - Configuración adicional de axios
   * @returns {Promise} Promesa con respuesta de axios
   */
  patch: (endpoint, pathParams = {}, data = {}, config = {}) => {
    const url = buildUrl(endpoint, pathParams);
    return axios.patch(url, data, {
      headers: getAuthHeaders(),
      ...config,
    });
  },

  /**
   * DELETE request
   *
   * @param {string} endpoint - Endpoint de API_ENDPOINTS
   * @param {Object} pathParams - Parámetros de ruta (ej: { plan_id: 123 })
   * @param {Object} data - Datos a enviar en el body (opcional)
   * @param {Object} config - Configuración adicional de axios
   * @returns {Promise} Promesa con respuesta de axios
   */
  delete: (endpoint, pathParams = {}, data = {}, config = {}) => {
    const url = buildUrl(endpoint, pathParams);
    return axios.delete(url, {
      headers: getAuthHeaders(),
      data,
      ...config,
    });
  },
};

/**
 * Utilidades adicionales
 */
export const apiUtils = {
  buildUrl,
  getBaseApiUrl,
  getAuthHeaders,
  handleError,

  /**
   * Helper para descargar archivos blob
   */
  downloadFile: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Helper para convertir respuesta de error en objeto legible
   */
  extractErrorMessage: (error) => {
    const handled = handleError(error);
    return handled.message;
  },
};

export default apiClient;
